/**
 * State for the workspace floating chat — the FAB + floating window that hosts
 * chats started from the home and workspace-detail surfaces (the workspace-level
 * twin of the canvas floating chat).
 *
 * Owns two things:
 *  - **Session navigation**: an in-memory stack (push/replace/back). `currentSessionId`
 *    is the top of the stack (or null). An open window with an empty stack is a
 *    *draft* — the composer is shown and mints its session on first send.
 *  - **Surface state**: `isOpen` (the window is up) vs closed (just the FAB launcher),
 *    plus a persisted `sizeClass` (`windowed` floating panel vs `full-height` docked).
 *
 * Unlike the canvas provider (which remounts per canvas), this mounts once in
 * `WorkspaceLayout`, so the rail, tab bar, browsers, and composer all consume it
 * via plain context. State survives in-app navigation; the panel simply stops
 * rendering on routes where the surface isn't active (see
 * `useIsFloatingChatRouteActive`) and restores when the user returns.
 */

import {
  useFloatingChatSessionStack,
  type FloatingChatSessionHistory,
  type FloatingChatSizeClass,
} from "@/components/FloatingChat/useFloatingChatSessionStack";
import { ROUTES } from "@/constants/routes";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useNewHomeEnabled } from "@/pages/workspaces/home/new-home/flag";
import { useEventStream } from "@/providers/EventStreamProvider";
import type { EventStreamEvent } from "@/types/events";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useMatch } from "react-router-dom";

export type WorkspaceFloatingSessionHistory = FloatingChatSessionHistory;

/** Window size class: a small floating panel vs a full-height docked panel. */
export type WorkspaceChatSizeClass = FloatingChatSizeClass;

export interface WorkspaceFloatingChatContextValue {
  /** Top-of-stack session id, or null (null while the window is a draft). */
  currentSessionId: string | null;
  canGoBack: boolean;
  /** True when the window is up (a session OR a draft); false shows only the FAB. */
  isOpen: boolean;
  /** True when the window is up with no session yet — the composer mints one on send. */
  isDraft: boolean;
  /** windowed (floating panel) vs full-height (docked). Persisted globally. */
  sizeClass: WorkspaceChatSizeClass;

  /** Open the window onto a session. Second arg defaults to `"push"`. */
  openSession: (
    sessionId: string,
    history?: WorkspaceFloatingSessionHistory,
  ) => void;
  goBack: () => void;
  /** Minimize to the FAB, keeping the session stack so reopening restores context. */
  collapse: () => void;
  /** Close to the FAB and clear session history. */
  closeChat: () => void;
  /** Flip between the windowed and full-height size classes. */
  toggleSizeClass: () => void;
  /** Open the window with an empty stack (a fresh draft chat). */
  startDraft: () => void;
  /**
   * Open the FAB launcher onto something: re-show the current session if the
   * stack has one, else open `fallbackSessionId` (most-recent in scope) if
   * given, else start a draft.
   */
  expand: (fallbackSessionId?: string) => void;
}

const WorkspaceFloatingChatContext =
  createContext<WorkspaceFloatingChatContextValue | null>(null);

export function useWorkspaceFloatingChat(): WorkspaceFloatingChatContextValue {
  const ctx = useContext(WorkspaceFloatingChatContext);
  if (!ctx) {
    throw new Error(
      "useWorkspaceFloatingChat must be used within WorkspaceFloatingChatProvider",
    );
  }
  return ctx;
}

/**
 * Non-throwing accessor for surfaces that may render outside the provider
 * (e.g. the legacy layout). Returns null when the provider is absent, letting
 * `useOpenChat` fall back to plain navigation.
 */
export function useWorkspaceFloatingChatOptional(): WorkspaceFloatingChatContextValue | null {
  return useContext(WorkspaceFloatingChatContext);
}

/**
 * Width (px) the full-height docked chat reserves on the right so the main
 * content narrows beside it instead of being overlapped (0 unless docked open).
 *
 * Kept in its own context so the panel's per-frame width updates while a user
 * drag-resizes the dock don't invalidate the main chat context — which would
 * re-render every `useOpenChat` consumer (browser rows, rail, …). Only the panel
 * (writer) and the layout's main region (reader) subscribe here.
 */
interface WorkspaceFloatingChatLayoutValue {
  dockReservedWidthPx: number;
  setDockReservedWidthPx: (px: number) => void;
}

const WorkspaceFloatingChatLayoutContext =
  createContext<WorkspaceFloatingChatLayoutValue>({
    dockReservedWidthPx: 0,
    setDockReservedWidthPx: () => {},
  });

export function useWorkspaceFloatingChatLayout(): WorkspaceFloatingChatLayoutValue {
  return useContext(WorkspaceFloatingChatLayoutContext);
}

/**
 * Route + viewport gate: the floating window is a desktop (lg+) pattern for the
 * home and workspace-detail surfaces only (on canvas the canvas owns the corner,
 * and the full-page chat route is already fullscreen). Deliberately excludes the
 * right-pane state — the panel handles that itself so a full-height dock can
 * coexist beside an expanded pane.
 */
export function useIsFloatingChatRouteActive(): boolean {
  const isDesktop = useBreakpoint("lg");
  const isHome = !!useMatch(ROUTES.HOME);
  const isWorkspaceDetail = !!useMatch(ROUTES.WORKSPACES.DETAIL);
  // The new home has no floating chat: its send starts a canvas.
  const newHome = useNewHomeEnabled();
  return isDesktop && ((isHome && !newHome) || isWorkspaceDetail);
}

/**
 * True when a *new* chat should open in the floating window rather than a tab.
 * Equals the route gate (canvas parity): while the right pane is expanded
 * full-width the panel relocates to the LEFT of it instead of hiding
 * (full-height docks beside the pane, windowed floats over it — see
 * `WorkspaceFloatingChatPanel`), so fresh opens land in the floating surface
 * there too. Kept as a separate seam from the route gate in case open policy
 * ever diverges from render policy again.
 */
export function useIsFloatingChatSurfaceActive(): boolean {
  return useIsFloatingChatRouteActive();
}

export function WorkspaceFloatingChatProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { registerHandler } = useEventStream();

  // Right-side space reserved by the full-height dock (0 otherwise).
  const [dockReservedWidthPx, setDockReservedWidthPx] = useState(0);

  // Shared stack/open/size-class machine. One storage key — this is a single
  // layout-level instance, not per-workspace like canvas. Defaults to the
  // full-height dock: a chat initiated from home/workspace detail reads as a
  // right pane until the user chooses the windowed panel.
  const {
    stack,
    currentSessionId,
    canGoBack,
    open,
    sizeClass,
    openSession,
    goBack,
    collapse,
    closeChat,
    toggleSizeClass,
    setStack,
    setOpen,
  } = useFloatingChatSessionStack({
    sizeClassStorageKey: "workspace_floating_chat_size_class",
    defaultSizeClass: "full-height",
  });

  const isDraft = open && stack.length === 0;

  const startDraft = useCallback(() => {
    setStack([]);
    setOpen(true);
  }, [setStack, setOpen]);

  const expand = useCallback(
    (fallbackSessionId?: string) => {
      if (currentSessionId) {
        setOpen(true);
        return;
      }
      if (fallbackSessionId) {
        openSession(fallbackSessionId, "replace");
        return;
      }
      startDraft();
    },
    [currentSessionId, openSession, startDraft],
  );

  // Eject a session from the stack when it's deleted or archived (mirrors
  // DeletedResourceTabSync for tabs). If that empties a non-empty stack, close
  // to the FAB rather than dropping into a draft.
  const stackRef = useRef(stack);
  stackRef.current = stack;
  useEffect(() => {
    const handler = (event: EventStreamEvent) => {
      const isRemoval =
        (event.type === "flow_session.updated" &&
          !!event.data.flow_session?.archived_at) ||
        event.type === "flow_session.deleted";
      if (!isRemoval) return;
      const removedId = event.data.flow_session?.id
        ? String(event.data.flow_session.id)
        : undefined;
      if (!removedId) return;

      const prev = stackRef.current;
      if (!prev.includes(removedId)) return;
      const next = prev.filter((id) => id !== removedId);
      setStack(next);
      if (next.length === 0) setOpen(false);
    };
    return registerHandler(handler);
  }, [registerHandler, setStack, setOpen]);

  const value = useMemo<WorkspaceFloatingChatContextValue>(
    () => ({
      currentSessionId,
      canGoBack,
      isOpen: open,
      isDraft,
      sizeClass,
      openSession,
      goBack,
      collapse,
      closeChat,
      toggleSizeClass,
      startDraft,
      expand,
    }),
    [
      currentSessionId,
      canGoBack,
      open,
      isDraft,
      sizeClass,
      openSession,
      goBack,
      collapse,
      closeChat,
      toggleSizeClass,
      startDraft,
      expand,
    ],
  );

  const layoutValue = useMemo<WorkspaceFloatingChatLayoutValue>(
    () => ({ dockReservedWidthPx, setDockReservedWidthPx }),
    [dockReservedWidthPx],
  );

  return (
    <WorkspaceFloatingChatContext.Provider value={value}>
      <WorkspaceFloatingChatLayoutContext.Provider value={layoutValue}>
        {children}
      </WorkspaceFloatingChatLayoutContext.Provider>
    </WorkspaceFloatingChatContext.Provider>
  );
}
